import { Filesystem, Directory } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';

const IMAGE_DIR = 'ead-images';

// Índice em memória dos filenames já presentes em IMAGE_DIR. Existe pra evitar
// um Filesystem.stat() por imagem só pra testar presença: quando o arquivo não
// existe, stat() rejeita com OS-PLUG-FILE-0008 e o @capacitor/filesystem loga
// toda rejeição no console nativo do iOS como "ERROR MESSAGE: ...". Abrir uma
// listagem de cursos dispara dezenas dessas (uma por imagem). Um único readdir
// no primeiro acesso resolve todas as checagens; o índice é mantido em sincronia
// ao gravar e é reconstruído a cada boot (reflete o disco no readdir inicial).
let _index = null;
let _indexPromise = null;

// Negative-cache de sessão: filenames que deram 4xx (curso sem imagem no
// servidor). Sem isso, o _cacheEnrollImages re-buscava as mesmas imagens 404 a
// cada getUserEnroll (toda navegação) pra sempre. Em memória → reseta no restart
// (uma imagem subida depois aparece no próximo boot).
const _failed = new Set();
// Dedup de fetches em voo por filename → chamadas concorrentes pra mesma imagem
// compartilham uma única requisição/escrita.
const _inflight = new Map();

async function ensureIndex () {
  if (_index) return _index;
  if (_indexPromise) return _indexPromise;
  _indexPromise = (async () => {
    try {
      const { files } = await Filesystem.readdir({ path: IMAGE_DIR, directory: Directory.Data });
      // @capacitor/filesystem v8 devolve FileInfo[] (.name); versões antigas
      // devolviam string[]. Cobre ambos.
      _index = new Set((files || []).map(f => (f && f.name) || f));
    } catch {
      // Diretório ainda não criado (nenhuma imagem cacheada) — índice vazio.
      _index = new Set();
    }
    return _index;
  })();
  return _indexPromise;
}

function urlToFilename (url) {
  try {
    const pathname = new URL(url).pathname; // e.g. '/209681/courses/62648.png'
    return pathname.replace(/^\//, '').replace(/\//g, '_'); // '209681_courses_62648.png'
  } catch {
    return null;
  }
}

async function uriFor (path) {
  const { uri } = await Filesystem.getUri({ path, directory: Directory.Data });
  return Capacitor.convertFileSrc(uri);
}

export const imageCache = {
  async cacheImage (url) {
    if (!url || !Capacitor.isNativePlatform()) return null;
    // Só cacheia URL remota http(s). Pula relativas/bundladas e, sobretudo, URLs
    // já-locais (convertFileSrc gera '_capacitor_file_'): re-cachear o local criava
    // arquivo-lixo + entrada duplicada no imageMap (acontecia quando o getUserEnroll
    // offline repopulava os enrolls já reescritos pra local).
    if (!/^https?:\/\//i.test(url) || url.includes('_capacitor_file_')) return null;

    const filename = urlToFilename(url);
    if (!filename) return null;

    // Imagem que já deu 4xx nesta sessão → não re-busca.
    if (_failed.has(filename)) return null;

    // Já tem um fetch em voo pra esse filename → reusa (sem requisição duplicada).
    if (_inflight.has(filename)) return _inflight.get(filename);
    const pending = _resolveImage(url, filename);
    _inflight.set(filename, pending);
    try {
      return await pending;
    } finally {
      _inflight.delete(filename);
    }
  },

  // Apaga arquivos-lixo de ead-images cujo nome começa com '_capacitor_file_'
  // (resíduo do re-cache de URL já-local pré-f3f03d4). Idempotente; após a 1ª
  // passada não sobra nenhum. Mantém o índice em memória em sincronia.
  async gcLocalImageFiles () {
    if (!Capacitor.isNativePlatform()) return;
    const index = await ensureIndex();
    const garbage = [...index].filter(n => n && n.startsWith('_capacitor_file_'));
    for (const name of garbage) {
      try {
        await Filesystem.deleteFile({ path: `${IMAGE_DIR}/${name}`, directory: Directory.Data });
        index.delete(name);
      } catch {
        // arquivo já sumiu entre o readdir e o delete — ignora.
      }
    }
  }
};

// Resolve a imagem: devolve do disco se já cacheada (com stat de sanidade), senão
// baixa, grava e indexa. 4xx vai pro negative-cache; 5xx/erro de rede não (pode
// ser transitório → permite nova tentativa). Nunca lança (retorna null no erro).
async function _resolveImage (url, filename) {
  const path = `${IMAGE_DIR}/${filename}`;

  const index = await ensureIndex();
  if (index.has(filename)) {
    // Confirma que o arquivo ainda está no disco antes de devolver a URI.
    // User pode ter limpado o cache do app via Settings (Android Storage)
    // sem matar o processo — IMAGE_DIR vai pro lixo mas o índice em memória
    // continua marcando os filenames como presentes, e devolveríamos uma URI
    // que aponta pra arquivo inexistente (image broken sem fallback).
    try {
      await Filesystem.stat({ path, directory: Directory.Data });
      return uriFor(path);
    } catch {
      index.delete(filename);
    }
  }

  try {
    const base64 = await fetchAsBase64(url);
    await Filesystem.writeFile({ path, data: base64, directory: Directory.Data, recursive: true });
    index.add(filename);
    return uriFor(path);
  } catch (e) {
    if (e && e.status >= 400 && e.status < 500) _failed.add(filename);
    return null;
  }
}

async function fetchAsBase64 (url) {
  const response = await fetch(url);
  if (!response.ok) {
    const err = new Error(`HTTP ${response.status}`);
    err.status = response.status;
    throw err;
  }
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
